import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Grid } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import {
  ButtonWithLoader,
  type Field,
  PuiTextField,
  Text,
} from '@pbt/pbt-ui-components'
import AddressSearch from '@pbt/pbt-ui-components/src/components/inputs/AddressSearch'
import { useAddressSearchWithExperian } from '@pbt/pbt-ui-components/src/components/inputs/useAddressSearchWithExperian'
import type { ExperianAddress } from '@pbt/pbt-ui-components/src/types/address'

import { AlertBanner } from '~/components/elements/AlertBanner/AlertBanner'
import Typography from '~/components/elements/Typography/Typography'

const useStyles = makeStyles(
  (theme) => ({
    formContent: {
      backgroundColor: theme.colors.brandLight,
      padding: 16,
      borderRadius: 4,
    },
    button: {
      width: '100%',
      '&:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: '2px',
      },
    },
  }),
  { name: 'AddNewPracticeForm' },
)

export interface AddNewPracticeFormProps {
  cancelLabel?: string
  fields: {
    aptSuiteUnitBuilding: Field
    city: Field
    emailAddress: Field
    phoneNumber: Field
    previousPracticeName: Field
    state: Field
    streetAddress: Field
  }
  isFormOpen?: boolean
  isValidationTriggeredExternally?: boolean
  onCancel: () => void
  onSubmit: () => void
  submitLabel?: string
  title?: string
}

const AddNewPracticeForm = ({
  cancelLabel,
  fields,
  isFormOpen = true,
  isValidationTriggeredExternally = false,
  onCancel,
  onSubmit,
  submitLabel,
  title,
}: AddNewPracticeFormProps) => {
  const classes = useStyles()
  const { t } = useTranslation(['Common', 'TimeTable'])

  const {
    previousPracticeName,
    streetAddress,
    aptSuiteUnitBuilding,
    city,
    state,
    phoneNumber,
    emailAddress,
  } = fields

  const { onSearch, onGetFormattedAddress: originalOnGetFormattedAddress } =
    useAddressSearchWithExperian()
  const [isLoadingAddress, setIsLoadingAddress] = useState(false)

  const handleGetFormattedAddress = async (formatUrl: string) => {
    setIsLoadingAddress(true)
    try {
      return await originalOnGetFormattedAddress(formatUrl)
    } finally {
      setIsLoadingAddress(false)
    }
  }

  const handleSuggestionSelected = (addressData: ExperianAddress) => {
    streetAddress.setValue(addressData.line1 || '')
    aptSuiteUnitBuilding.setValue(addressData.line2 || '')
    city.setValue(addressData.city || '')
    state.setValue(addressData.state || '')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit()
  }

  const errorMessage = useMemo(() => {
    if (isFormOpen && isValidationTriggeredExternally) {
      return t(
        'TimeTable:PMR_CONSENT.PLEASE_ADD_PRACTICE_BEFORE_FINALIZING_ERROR',
      )
    }

    if (previousPracticeName.open && !previousPracticeName.valid) {
      return t('TimeTable:PMR_CONSENT.PLEASE_ENTER_PRACTICE_NAME_ERROR')
    }
    if (streetAddress.open && !streetAddress.valid) {
      return t('TimeTable:PMR_CONSENT.PLEASE_ENTER_STREET_ADDRESS_ERROR')
    }
    if (city.open && !city.valid) {
      return t('TimeTable:PMR_CONSENT.PLEASE_ENTER_CITY_ERROR')
    }
    if (state.open && !state.valid) {
      return t('TimeTable:PMR_CONSENT.PLEASE_ENTER_STATE_ERROR')
    }
    if (phoneNumber.open && !phoneNumber.valid) {
      return t('TimeTable:PMR_CONSENT.PLEASE_ENTER_PHONE_NUMBER_ERROR')
    }
    return null
  }, [
    previousPracticeName.open,
    previousPracticeName.valid,
    streetAddress.open,
    streetAddress.valid,
    city.open,
    city.valid,
    state.open,
    state.valid,
    phoneNumber.open,
    phoneNumber.valid,
    isFormOpen,
    isValidationTriggeredExternally,
    t,
  ])

  const hasErrors =
    errorMessage !== null || (isFormOpen && isValidationTriggeredExternally)

  return (
    <form className={classes.formContent} onSubmit={handleSubmit}>
      <Text pb={1} variant="h2">
        {title || t('TimeTable:PMR_CONSENT.ADD_NEW_PRACTICE_TITLE')}
      </Text>
      <Grid container direction="column" spacing={2}>
        {hasErrors && (
          <Grid item>
            <AlertBanner
              fullWidth
              content={
                <Typography.H3 color="uiError">{errorMessage}</Typography.H3>
              }
              variant="error"
            />
          </Grid>
        )}
        <Grid item>
          <PuiTextField
            field={previousPracticeName}
            label={`${t('TimeTable:PMR_CONSENT.PREVIOUS_PRACTICE_NAME_LABEL')}*`}
            margin="none"
          />
        </Grid>
        <Grid item>
          <AddressSearch
            error={streetAddress.open && !streetAddress.valid}
            helperText={streetAddress.message}
            label={`${t('TimeTable:PMR_CONSENT.STREET_ADDRESS_LABEL')}*`}
            value={streetAddress.value}
            onChange={(value: string) => streetAddress.setValue(value)}
            onGetFormattedAddress={handleGetFormattedAddress}
            onSearch={onSearch}
            onSuggestionSelected={handleSuggestionSelected}
          />
        </Grid>
        <Grid item>
          <PuiTextField
            field={aptSuiteUnitBuilding}
            isLoading={isLoadingAddress}
            label={t('TimeTable:PMR_CONSENT.APT_SUITE_UNIT_BUILDING_LABEL')}
            margin="none"
          />
        </Grid>
        <Grid container item spacing={2}>
          <Grid item md={9} xs={12}>
            <PuiTextField
              field={city}
              isLoading={isLoadingAddress}
              label={`${t('TimeTable:PMR_CONSENT.CITY_LABEL')}*`}
              margin="none"
            />
          </Grid>
          <Grid item md={3} xs={12}>
            <PuiTextField
              field={state}
              isLoading={isLoadingAddress}
              label={`${t('TimeTable:PMR_CONSENT.STATE_LABEL')}*`}
              margin="none"
            />
          </Grid>
        </Grid>
        <Grid container item md={6}>
          <Grid item md={4}>
            <PuiTextField
              field={phoneNumber}
              label={`${t('TimeTable:PMR_CONSENT.PHONE_NUMBER_LABEL')}*`}
              margin="none"
              type="tel"
            />
          </Grid>
        </Grid>
        <Grid item>
          <PuiTextField
            field={emailAddress}
            label={t('TimeTable:PMR_CONSENT.EMAIL_ADDRESS_LABEL')}
            margin="none"
            type="email"
          />
        </Grid>
        <Grid container item gap={2} mt={2}>
          <Grid item md={4}>
            <ButtonWithLoader
              className={classes.button}
              color="primary"
              type="submit"
            >
              {submitLabel || t('TimeTable:PMR_CONSENT.ADD_PRACTICE_BUTTON')}
            </ButtonWithLoader>
          </Grid>
          <Grid item md={3}>
            <ButtonWithLoader
              className={classes.button}
              color="secondary"
              type="button"
              variant="outlined"
              onClick={onCancel}
            >
              {cancelLabel || t('Common:CANCEL_ACTION')}
            </ButtonWithLoader>
          </Grid>
        </Grid>
      </Grid>
    </form>
  )
}

export default AddNewPracticeForm
